import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { CenteredLayout } from '#components/patterns/layouts/centered/CenteredLayout.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useSuspenseAllOIDCProvidersQuery } from '#screens/settings/sso/sso-page/SSOSettingsPage.queries.ts';
import { DownloadLinkSnippet } from '../downloads/DownloadLinkSnippet.tsx';

const docsLink = (
  <a className="a" href="https://www.defined.net/docs/guides/setting-up-sso/?tab=endpoint" target="_blank" rel="help">
    Setting Up SSO
  </a>
);

const ENDPOINT_PROVIDERS_PATH = '/settings/sso?tab=endpoint';

/**
 * {@link EmptyEndpointUsers} is the empty state for the users page. Nobody can become a user without an enabled
 * endpoint provider, so that case leads with adding or enabling one; with one enabled, it hands over the client
 * download for the first enrollment.
 */
export function EmptyEndpointUsers() {
  const location = useAppLocation();
  const endpointProviders = useSuspenseAllOIDCProvidersQuery().data.endpoint.data;
  const enabledProviders = endpointProviders.filter((provider) => provider.enabled);

  if (endpointProviders.length === 0) {
    return (
      <CenteredLayout preventNotifications={true}>
        <div>
          <h2 className="h3 mb-16">No users yet</h2>
          <p className="p-lt mb-24">
            Users appear here once someone enrolls a device by signing in through an endpoint OIDC provider. Add a
            provider to let people enroll their own devices. For more information, read {docsLink}.
          </p>
          <LinkButton to="/settings/sso/add?type=endpoint" state={{ from: location }}>
            Add endpoint OIDC provider
          </LinkButton>
        </div>
      </CenteredLayout>
    );
  }

  if (enabledProviders.length === 0) {
    const onlyProvider = endpointProviders.length === 1 ? endpointProviders[0] : undefined;

    return (
      <CenteredLayout preventNotifications={true}>
        <div>
          <h2 className="h3 mb-16">
            {onlyProvider ? 'Your endpoint OIDC provider is disabled' : 'Your endpoint OIDC providers are disabled'}
          </h2>
          <p className="p-lt mb-24">
            {onlyProvider ?
              <>
                Nobody can sign in through <strong>{onlyProvider.name}</strong> until it&rsquo;s enabled.
              </>
            : <>Nobody can sign in until one of them is enabled.</>}{' '}
            Enable a provider to let people enroll their devices from DNClient Desktop. For more information, read{' '}
            {docsLink}.
          </p>
          <LinkButton to={ENDPOINT_PROVIDERS_PATH}>Enable endpoint OIDC provider</LinkButton>
        </div>
      </CenteredLayout>
    );
  }

  const onlyProvider = enabledProviders.length === 1 ? enabledProviders[0] : undefined;

  return (
    <CenteredLayout preventNotifications={true}>
      <div>
        <h2 className="h3 mb-16">No one has enrolled yet</h2>
        <p className="p-lt">
          {onlyProvider ?
            <>
              Your endpoint OIDC provider{' '}
              <Link className="a" to={ENDPOINT_PROVIDERS_PATH}>
                {onlyProvider.name}
              </Link>{' '}
              is set up. Users appear here once someone signs in through it from DNClient Desktop to enroll a device.
            </>
          : <>
              Your{' '}
              <Link className="a" to={ENDPOINT_PROVIDERS_PATH}>
                endpoint OIDC providers
              </Link>{' '}
              are set up. Users appear here once someone signs in through one of them from DNClient Desktop to enroll a
              device.
            </>
          }
        </p>
        <ul className="ul-lt">
          <li>Users are added on their first sign-in from DNClient Desktop.</li>
          <li>Each user&rsquo;s hosts are linked from this list.</li>
          <li>Share the download with your team to get started.</li>
        </ul>
        <p className="mb-8">
          <strong>DNClient Desktop</strong>
        </p>
        <DownloadLinkSnippet />
      </div>
    </CenteredLayout>
  );
}
